import { useState, useEffect } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Target, CheckCircle2, XCircle, AlertTriangle, History } from "lucide-react";
import { useFb } from "../../utils/football";
import { hapticLight } from "../../utils/haptics";
import { toast } from "sonner";

const Group = ({ title, children }) => (
  <div className="mt-5">
    <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted-foreground">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const QuestionCard = ({ q, onAnswer }) => {
  const resolved = q.resolved;
  const didNotAnswer = resolved && (q.user_answer == null || q.user_answer === "");
  const gotItRight = resolved && !didNotAnswer && q.user_points > 0;
  return (
    <div
      className={`rounded-xl border p-4 ${didNotAnswer ? "border-orange-500/40 bg-orange-500/5" : "border-border bg-card"}`}
      data-testid={`predictor-q-${q.id}`}
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-bold">{q.question_text}</span>
        <span className="text-[11px] font-bold wc-gold">+{q.points_if_correct}</span>
      </div>
      {q.options?.length > 0 ? (
        <div className="mt-2 grid grid-cols-3 gap-2">
          {q.options.map((opt) => {
            const selected = q.user_answer === opt;
            const isCorrect = resolved && q.correct_answer === opt;
            const isWrong = resolved && selected && q.correct_answer !== opt;
            // When the user did NOT answer, show the correct option in a neutral
            // orange style so it's not mistaken for a green "you got it right".
            const cls = didNotAnswer && isCorrect
              ? "border-orange-400/60 bg-orange-400/10 text-orange-200"
              : isCorrect ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
              : isWrong ? "border-red-500 bg-red-500/15 text-red-300"
              : selected ? "border-amber-400 bg-amber-400/15 wc-gold" : "border-border bg-background";
            return (
              <button
                key={opt}
                disabled={resolved}
                onClick={() => onAnswer(q, opt)}
                className={`rounded-lg border py-2 text-xs font-bold ${cls}`}
                data-testid={`predictor-opt-${q.id}-${opt}`}
              >
                {opt.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
      ) : (
        <ScoreInput q={q} onAnswer={onAnswer} resolved={resolved} />
      )}
      {resolved && (
        didNotAnswer ? (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-orange-300" data-testid={`predictor-noanswer-${q.id}`}>
            <AlertTriangle size={14} />
            <span>You did not answer · Correct answer: <b className="text-foreground">{q.correct_answer ?? "—"}</b></span>
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-1.5 text-xs">
            {gotItRight ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-red-400" />}
            <span className="text-muted-foreground">Your answer: <b className="text-foreground">{q.user_answer}</b> · Correct: <b className="text-foreground">{q.correct_answer ?? "—"}</b> · {q.user_points || 0} pts</span>
          </div>
        )
      )}
    </div>
  );
};

const ScoreInput = ({ q, onAnswer, resolved }) => {
  const [v, setV] = useState(q.user_answer || "");
  // Keep the field in sync if the answer arrives/changes on (re)load or day switch.
  useEffect(() => { setV(q.user_answer || ""); }, [q.user_answer, q.id]);
  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <input value={v} onChange={(e) => setV(e.target.value)} placeholder="e.g. 2-1" disabled={resolved}
          className="h-10 flex-1 rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-amber-400"
          data-testid={`predictor-score-${q.id}`} />
        {!resolved && (
          <button disabled={!v} onClick={() => onAnswer(q, v)} className="rounded-lg wc-bg-gold px-4 text-sm font-bold text-slate-900 disabled:opacity-50">Submit</button>
        )}
      </div>
      {!resolved && q.user_answer && (
        <p className="mt-1.5 text-xs text-muted-foreground" data-testid={`predictor-your-prediction-${q.id}`}>
          Your prediction: <b className="wc-gold">{q.user_answer}</b>
        </p>
      )}
    </div>
  );
};

export default function FootballPredictorPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDay = async (md) => {
    try {
      const d = await fb.get(`/football/predictor/${md}`);
      setQuestions(d.questions || []);
      setSelectedDay(d.match_day || md);
    } catch { setQuestions([]); }
  };

  const load = async () => {
    try {
      const d = await fb.get("/football/predictor/active");
      setQuestions(d.questions || []);
      setDays(d.days || []);
      setSelectedDay(d.match_day || null);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onAnswer = async (q, answer) => {
    hapticLight();
    try {
      await fb.post("/football/predictor/answer", { question_id: q.id, answer });
      setQuestions((prev) => prev.map((x) => x.id === q.id ? { ...x, user_answer: answer } : x));
      toast.success("Prediction saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const fmtDay = (md) => { try { return new Date(md + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }); } catch { return md; } };

  const perMatch = questions.filter((q) => q.fixture_id && ["match_result", "exact_score"].includes(q.question_type));
  const weekly = questions.filter((q) => q.is_weekly_bonus);
  const dayLevel = questions.filter((q) => !perMatch.includes(q) && !weekly.includes(q));

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="predictor-page">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="predictor-back"><ChevronLeft size={18} /> Home</button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Target size={20} className="text-amber-300" /><h1 className="text-xl font-extrabold text-white">Predictor</h1></div>
          <button onClick={() => navigate("/football/predictor/history")} className="inline-flex items-center gap-1 text-xs font-bold text-white/85" data-testid="predictor-history-link"><History size={15} /> My History</button>
        </div>
      </div>
      <div className="mx-auto max-w-md px-4">
        {days.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="predictor-day-picker">
            {days.map((md) => (
              <button
                key={md}
                onClick={() => loadDay(md)}
                className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-bold transition-all ${selectedDay === md ? "wc-bg-gold border-amber-400 text-slate-900" : "border-border bg-card text-muted-foreground"}`}
                data-testid={`predictor-day-${md}`}
              >
                {fmtDay(md)}
              </button>
            ))}
          </div>
        )}
        {loading ? <div className="mt-6 h-40 animate-pulse rounded-xl bg-secondary/50" /> :
          questions.length === 0 ? <div className="mt-10 text-center text-sm text-muted-foreground" data-testid="predictor-empty">No questions available yet. Check back closer to kickoff.</div> : (
            <>
              {perMatch.length > 0 && <Group title="Match Predictions">{perMatch.map((q) => <QuestionCard key={q.id} q={q} onAnswer={onAnswer} />)}</Group>}
              {dayLevel.length > 0 && <Group title="Match Day">{dayLevel.map((q) => <QuestionCard key={q.id} q={q} onAnswer={onAnswer} />)}</Group>}
              {weekly.length > 0 && <Group title="⭐ Weekly Bonus">{weekly.map((q) => <QuestionCard key={q.id} q={q} onAnswer={onAnswer} />)}</Group>}
            </>
          )}
      </div>
    </div>
  );
}
